<template>
  <div class="entity-entry-table">
    <b-table
      id="EntityEntryTable"
      :busy="is.loading"
      :fields="entitiesTable.fields"
      :items="entitiesTable.items"
      striped
      hover
      show-empty
      :tbody-tr-class="table.helper.rowClass"

      sort-icon-left
      :no-local-sorting="searchable"
      :sort-by.sync="table.sort.by"
      :sort-desc.sync="table.sort.desc"
      @sort-changed="changeSort"

      @head-clicked="table.events.headClicked"
      @row-clicked="table.events.rowClicked"
      @row-contextmenu="table.events.rowContextMenu"
      @row-dblclicked="table.events.rowDblClicked"
      @row-middle-clicked="table.events.rowMiddleClicked"
      @row-hovered="table.events.rowHovered"
      @row-unhovered="table.events.rowUnhovered"
    >
      <template #table-busy>
        <div class="text-center">
          <loader type="dots" inline scale="4"/>
          <div>{{ $t('components.table.busy') }}</div>
        </div>
      </template>

      <template #empty>
        <p class="my-2 text-center">{{ $t('components.table.empty') }}</p>
      </template>

      <template #emptyfiltered>
        <p class="my-2 text-center">{{ $t('components.table.emptyfiltered') }}</p>
      </template>

      <template v-for="slotName in $cellSlots" #[slotName]="data">
        <slot :name="slotName" :cell="data" :definition="definition">
          <div :key="slotName" v-html="valueFormatter(definition, data.field.key, data.value, hitColumnTypeMap[data.field.key]).html"></div>
        </slot>
      </template>

      <template #cell(status)="data">
        <entity-state :entityKey="entityKey" :value="data.value"/>
      </template>
    </b-table>

    <b-pagination
      v-model="statistics.page.number"
      :per-page="statistics.page.size"
      :total-rows="statistics.total.items"
      :disabled="statistics.total.pages <= 1"
      align="center"
      aria-controls="EntityEntryTable"
      @change="getPage"
    />
  </div>
</template>

<script>
import { CLASS_TABLE_FIELD_KEY_UNKNOWN } from '@/assets/js/config/client'

import Table, { OPTIONS_BASE, TableHelper } from '@/assets/js/helper/table'
import { fieldToKeyConverter } from '@/assets/js/helper/search'
import { valueFormatter } from '@/assets/js/helper/entity'

import EntityState from '@/components/entity/State'

export default {
  name: 'EntityEntryTable',
  components: {
    EntityState
  },
  props: {
    entityKey: {
      type: String,
      required: true
    },
    entityBy: {
      type: Object,
      default: () => ({ name: null, id: null })
    },
    tableSettings: {
      type: Object,
      required: true
    },
    searchable: {
      type: Boolean,
      default: false
    },
    initialFilters: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    const tableOptions = this.tableSettings.options || {}
    const tableOptionFields = tableOptions.fields || {}

    return {
      tableOptionsKey: 0, // Key to force table re-render when options change
      table: {
        helper: this.tableSettings.helper || TableHelper,
        mapper: this.tableSettings.mapper || ((entities, definition) => entities),
        sort: {
          by: null,
          desc: false
        },
        options: Object.assign(tableOptions, {
          fields: Object.keys(OPTIONS_BASE).reduce((fieldOptions, fKey) => Object.assign(fieldOptions, { [fKey]: tableOptionFields[fKey] || OPTIONS_BASE[fKey] }), {})
        }),
        events: Object.assign({
          headClicked: () => {},
          rowClicked: () => {},
          rowContextMenu: () => {},
          rowDblClicked: () => {},
          rowMiddleClicked: () => {},
          rowHovered: () => {},
          rowUnhovered: () => {}
        }, this.tableSettings.events)
      }
    }
  },
  computed: {
    is () {
      return this.$store.getters[`${this.entityKey}/is`]
    },
    definition () {
      return this.$store.getters[`${this.entityKey}/definition`]
    },
    statistics () {
      return this.$store.getters[`${this.entityKey}/statistics`]
    },
    sorts () {
      return (this.searchable ? this.$store.getters[`${this.entityKey}/sorts`] : [])
        .map(s => Object.assign({ key: fieldToKeyConverter(s.field) }, s))
        .sort((a, b) => a.sortOrder - b.sortOrder)
    },
    hitColumns () {
      if (!this.searchable) return []
      
      const allHitColumns = this.$store.getters[`${this.entityKey}/hitColumns`] || []
      
      // Filter out _label columns that have a corresponding base column with the same sortOrder
      // e.g., exclude orderstate_label when orderstate exists with same sortOrder
      const columnsBySortOrder = {}
      const filteredColumns = []
      
      allHitColumns.forEach(hc => {
        const sortOrder = hc.sortOrder
        if (!columnsBySortOrder[sortOrder]) {
          columnsBySortOrder[sortOrder] = []
        }
        columnsBySortOrder[sortOrder].push(hc)
      })
      
      // For each sortOrder group, prefer non-_label columns, or _label if no base exists
      Object.keys(columnsBySortOrder).forEach(sortOrder => {
        const group = columnsBySortOrder[sortOrder]
        const labelCols = group.filter(hc => hc.name.endsWith('_label'))
        const baseCols = group.filter(hc => !hc.name.endsWith('_label'))
        
        if (baseCols.length > 0) {
          // Prefer base columns (e.g., orderstate with type "badge")
          filteredColumns.push(...baseCols)
        } else if (labelCols.length > 0) {
          // Use _label columns only if no base column exists
          filteredColumns.push(...labelCols)
        }
      })
      
      // Map columns
      return filteredColumns
        .map(hc => {
          const key = fieldToKeyConverter(hc.name)
          
          return {
            key,
            name: hc.name,
            displayName: hc.displayName,
            sortOrder: hc.sortOrder,
            type: hc.type,
            isRightAligned: hc.isRightAligned
          }
        })
        .sort((a, b) => a.sortOrder - b.sortOrder)
    },
    entities () {
      return this.is.loading ? [] : this.table.mapper(this.$store.getters[`${this.entityKey}/getUnwrapped`], this.definition)
    },
    hitColumnTypeMap () {
      // Map field keys to their hitColumn type for badge rendering
      const map = {}
      this.hitColumns.forEach(hc => {
        map[hc.key] = hc.type
      })
      return map
    },
    entitiesTable () {
      // Include tableOptionsKey in the dependency to force recomputation when options change
      const key = this.tableOptionsKey
      const fields = this.table.options.fields
      
      // Explicitly access labels to ensure reactivity tracking
      const labels = fields && fields.label ? fields.label : {}
      
      // Create a new options object to ensure labels are included
      const tableOptions = {
        ...fields,
        label: labels
      }
      
      return new Table(this.entities, tableOptions)
    },
    $cellSlots () {
      return this.table.options.fields.sorting.map(f => `cell(${f})`)
    }
  },
  methods: {
    valueFormatter,
    init () {
      if (this.searchable) {
        // Initialize labels object early to ensure it exists when table is first created
        if (!this.table.options.fields.label) {
          this.$set(this.table.options.fields, 'label', {})
        }
        
        this.$store.dispatch(`${this.entityKey}/initSearch`, { filters: this.initialFilters })
          .then(() => {
            this.setTableOptions()
          })
      } else {
        this.$store.dispatch(`${this.entityKey}/get`, { by: this.entityBy })
          .then(() => {
            this.setTableLabels()
          })
      }
    },
    setTableOptions () {
      if (this.searchable) {
        const DEFAULT_SORT = this.sorts.find(s => s.isDefault) || {}

        // Use hitColumns if available, otherwise fall back to sorts
        const COLUMNS = this.hitColumns.length > 0 ? this.hitColumns : this.sorts
        const FIELD_KEYS = COLUMNS.map(d => d.key)
        
        // Build labels object from scratch to ensure reactivity
        const FIELD_LABELS = {}
        
        // Extract and set labels for each column
        COLUMNS.forEach(d => {
          const displayName = d.displayName
          let label
          
          if (typeof displayName === 'object' && displayName !== null) {
            // Try current locale first
            let rawLabel = displayName[this.$i18n.locale]
            // Fallback to any available locale
            if (!rawLabel && Object.keys(displayName).length > 0) {
              rawLabel = Object.values(displayName)[0]
            }
            
            // Check if the label is a resource key (contains underscore, indicating it's a resource key like "Orders_HitColumn_OrderSource")
            // Resource keys typically have underscores and look like "Orders_HitColumn_OrderSource" or "Orders_OrderNumber"
            if (rawLabel && typeof rawLabel === 'string' && rawLabel.includes('_') && /^[A-Z][\w_]+$/.test(rawLabel)) {
              // Try to translate the resource key directly
              if (this.$i18n.te(rawLabel)) {
                label = this.$i18n.t(rawLabel)
              } else {
                // Try with common resource prefixes
                const resourceKeys = [
                  `Orders_${rawLabel}`,
                  `Orders_HitColumn_${rawLabel}`,
                  rawLabel
                ]
                for (const key of resourceKeys) {
                  if (this.$i18n.te(key)) {
                    label = this.$i18n.t(key)
                    break
                  }
                }
                // If translation not found, keep the resource key as-is (it will be handled by fallback)
                if (!label) {
                  label = rawLabel
                }
              }
            } else {
              // Not a resource key, use the label directly
              label = rawLabel
            }
            
            // If still no label, try entity-specific translation
            if (!label) {
              const translationKey = `views.${this.entityKey}.table.label.${d.key}`
              label = this.$i18n.te(translationKey) ? this.$i18n.t(translationKey) : null
            }
          } else if (typeof displayName === 'string') {
            label = displayName
          }
          
          // Final fallback: try translation key or use field key
          if (!label) {
            const translationKey = `views.${this.entityKey}.table.label.${d.key}`
            label = this.$i18n.te(translationKey) ? this.$i18n.t(translationKey) : d.key
          }
          
          FIELD_LABELS[d.key] = label
        })
        
        const FIELD_UNKNOWN_INDEX = Math.max(this.table.options.fields.sorting.findIndex(s => s === CLASS_TABLE_FIELD_KEY_UNKNOWN), 0)
        const SORTING_BEFORE_UNKNOWN = this.table.options.fields.sorting.slice(0, FIELD_UNKNOWN_INDEX)
        const SORTING_AFTER_UNKNOWN = this.table.options.fields.sorting.slice(FIELD_UNKNOWN_INDEX + 1)

        this.table.sort.by = DEFAULT_SORT.key || null
        this.table.sort.desc = DEFAULT_SORT.isDescending || false
        
        // Create a new fields object with all properties including the new labels
        const newFields = {
          ...this.table.options.fields,
          label: FIELD_LABELS,
          sorting: [].concat(SORTING_BEFORE_UNKNOWN, FIELD_KEYS, SORTING_AFTER_UNKNOWN),
          sortable: this.sorts.map(s => s.key)
        }
        
        // Replace the entire fields object to ensure reactivity
        this.$set(this.table.options, 'fields', newFields)
        
        // Increment the key to force the computed property to recompute
        this.tableOptionsKey++
      }
    },
    setTableLabels () {
      if (!this.searchable) {
        this.table.options.fields.label = this.definition.properties
          .reduce((labels, p) => Object.assign(labels, { [p.name]: p.translations[this.$i18n.locale].name }), {})
      }
    },
    getPage (number) {
      if (this.searchable) {
        this.$store.dispatch(`${this.entityKey}/getSearchPage`, { number })
      } else {
        this.$store.dispatch(`${this.entityKey}/getPage`, { by: this.entityBy, number })
      }
    },
    changeSort (tableSorting) {
      if (this.searchable) {
        const sort = this.sorts.find(s => s.key === tableSorting.sortBy)
        if (sort) this.$store.dispatch(`${this.entityKey}/setActiveSort`, { sortId: sort.id })
      }
    }
  },
  created () {
    this.init()
  },
  watch: {
    definition: {
      deep: true,
      handler () {
        this.setTableLabels()
      }
    },
    hitColumns: {
      handler () {
        if (this.searchable) {
          this.setTableOptions()
        }
      }
    }
  }
}
</script>

<style lang="scss"></style>
